import { Link, useLocation } from "@tanstack/react-router";
import { FileText, Handshake, SlidersHorizontal, Users } from "lucide-react";
import { cn } from "@/lib/utils";

const settingsTabs = [
  { label: "Users", href: "/dashboard/settings/users", icon: Users },
  { label: "System", href: "/dashboard/settings/system", icon: SlidersHorizontal },
  { label: "Templates", href: "/dashboard/settings/templates", icon: FileText },
  { label: "Tech Partners", href: "/dashboard/settings/tech-partners", icon: Handshake },
];

export function SettingsLayout({ children }: { children: React.ReactNode }) {
  const location = useLocation();

  return (
    <>
      {/* Settings header */}
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold">Settings</h1>
        <p className="mt-1 text-muted-foreground">Manage your workspace configuration.</p>
      </div>

      {/* Tab navigation */}
      <div className="relative mb-8">
        <div className="-mx-4 px-4 overflow-x-auto md:mx-0 md:px-0 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <nav className="flex gap-1 whitespace-nowrap">
            {settingsTabs.map((tab) => {
              const isActive = location.pathname === tab.href;

              return (
                <Link
                  key={tab.href}
                  to={tab.href}
                  role="tab"
                  aria-selected={isActive}
                  className={cn(
                    "group relative flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-colors rounded-md",
                    isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground/80",
                  )}
                >
                  <tab.icon
                    className={cn(
                      "h-4 w-4 transition-colors",
                      isActive
                        ? "text-primary"
                        : "text-muted-foreground/60 group-hover:text-muted-foreground",
                    )}
                  />
                  <span>{tab.label}</span>

                  {/* Active indicator — warm amber underline */}
                  {isActive && (
                    <span className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full bg-primary" />
                  )}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Baseline border */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-border" />
      </div>

      {/* Tab content */}
      {children}
    </>
  );
}
